import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import "./BitacoraProduccion.css";

// Material UI Icons
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ScienceIcon from '@mui/icons-material/Science';
import WineBarIcon from '@mui/icons-material/WineBar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GrassIcon from '@mui/icons-material/Grass';
import LayersIcon from '@mui/icons-material/Layers';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import TimelineIcon from '@mui/icons-material/Timeline';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import VerifiedIcon from '@mui/icons-material/Verified';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';

// Subcomponents de cada Sección
import CoccionSection from "./BitacoraProduccion/CoccionSection";
import MoliendaSection from "./BitacoraProduccion/MoliendaSection";
import DestilacionSection from "./BitacoraProduccion/DestilacionSection";
import GranelSection from "./BitacoraProduccion/GranelSection";
import ElaboracionSection from "./BitacoraProduccion/ElaboracionSection";
import MaduracionSection from "./BitacoraProduccion/MaduracionSection";
import CondicionesSection from "./BitacoraProduccion/CondicionesSection";
import QuejasSection from "./BitacoraProduccion/QuejasSection";

// Lista de instalaciones por defecto
const DEFAULT_INSTALACIONES = [
  {
    idanexocliente: "UP-042",
    id: "UP-042",
    alias: "Palenque Tradicional Santiago Matatlán (UP-042)",
    direccion: "Carretera Internacional Km 42, Santiago Matatlán, Oaxaca",
    informaciongeneralinstalacioncliente: {
      alias: "Palenque Tradicional Santiago Matatlán (UP-042)",
      direccion: "Carretera Internacional Km 42, Santiago Matatlán, Oaxaca"
    }
  },
  {
    idanexocliente: "UP-108",
    id: "UP-108",
    alias: "Fábrica Destilados del Valle (UP-108)",
    direccion: "Av. Ferrocarril No. 15, Tlacolula de Matamoros, Oaxaca",
    informaciongeneralinstalacioncliente: {
      alias: "Fábrica Destilados del Valle (UP-108)",
      direccion: "Av. Ferrocarril No. 15, Tlacolula de Matamoros, Oaxaca"
    }
  },
  {
    idanexocliente: "UP-019",
    id: "UP-019",
    alias: "Unidad de Producción San Dionisio (UP-019)",
    direccion: "Camino al Río S/N, San Dionisio Ocotepec, Oaxaca",
    informaciongeneralinstalacioncliente: {
      alias: "Unidad de Producción San Dionisio (UP-019)",
      direccion: "Camino al Río S/N, San Dionisio Ocotepec, Oaxaca"
    }
  }
];

// Lista de especies de maguey oficiales
const MAGUEY_SPECIES = [
  "Agave angustifolia Haw (Espadín)",
  "Agave potatorum Zucc (Tobalá)",
  "Agave americana L. (Arroqueño)",
  "Agave marmorata (Tepeztate)",
  "Agave rhodacantha (Mexicano)",
  "Agave karwinskii (Madrecuixe / Cuishe)",
  "Agave convallis (Jabalí)",
  "Agave seemanniana (Chiquilao)"
];

// Datos iniciales de demostración para el Front-End puro
const INITIAL_LOTS = [
  {
    id: "PROD-2026-001",
    noProduccion: "PROD-2026-001",
    unidadProduccion: "Palenque Tradicional Santiago Matatlán (UP-042)",
    predio: "El Pedregal - Paraje Las Flores (Guía No. G-8892)",
    tipoMaguey: "Agave angustifolia Haw (Espadín)",
    etapaActual: "granel",
    statusUI: "aprobado_ui",
    fechaInicio: "2026-07-10",
    fechaActualizacion: "2026-07-28",
    inspector: "Ing. Carlos Mendoza (UI Autorizado)",
    observacionesInspector: "Proceso completo verificado bajo NOM-070-SCFI-2016.",
    coccion: {
      fechaIngreso: "2026-07-10",
      guiaPredio: "G-8892 / Registro Predio: PR-OAX-104",
      numPinas: 420,
      kgMagueyCrudo: 9500,
      pctArt: 24.5,
      noInforme: "INF-COC-2026-081",
      tipoHorno: "Cónico bajo tierra con leña de encino",
      fechaInicioCoccion: "2026-07-11",
      fechaFinCoccion: "2026-07-16",
      kgMagueyCocido: 8200,
      observaciones: "Cocción tradicional a leña de encino durante 5 días."
    },
    molienda: {
      tipoMolienda: "Tahona de cantera jalada por bestia",
      fechaRegistro: "2026-07-18",
      items: [
        { id: 1, contenedor: "Tina 01 (Madera de Sabino)", kgMaguey: 2050, fechaFermentacion: "2026-07-18", volumenFormulado: 1200, fechaPrimeraDestilacion: "2026-07-22", volumenOrdinario: 450, pctAlcOrdinario: 26.5, volumenColas: 60 },
        { id: 2, contenedor: "Tina 02 (Madera de Sabino)", kgMaguey: 2050, fechaFermentacion: "2026-07-18", volumenFormulado: 1200, fechaPrimeraDestilacion: "2026-07-22", volumenOrdinario: 460, pctAlcOrdinario: 27.0, volumenColas: 55 },
        { id: 3, contenedor: "Tina 03 (Madera de Sabino)", kgMaguey: 2050, fechaFermentacion: "2026-07-19", volumenFormulado: 1200, fechaPrimeraDestilacion: "2026-07-23", volumenOrdinario: 440, pctAlcOrdinario: 26.0, volumenColas: 70 },
        { id: 4, contenedor: "Tina 04 (Madera de Sabino)", kgMaguey: 2050, fechaFermentacion: "2026-07-19", volumenFormulado: 1200, fechaPrimeraDestilacion: "2026-07-23", volumenOrdinario: 450, pctAlcOrdinario: 26.8, volumenColas: 65 }
      ],
      totalKgMaguey: 8200,
      totalVolFormulado: 4800,
      totalVolOrdinario: 1800,
      observaciones: "Fermentación espontánea con levaduras silvestres del entorno."
    },
    destilacion: {
      tipoAlambique: "Alambique de Cobre Tradicional (250 L)",
      fechaRegistro: "2026-07-24",
      items: [
        { id: 1, fechaSegunda: "2026-07-24", destiladorNum: "Alambique Cobre 01", volADestilar: 600, esDestiladoCon: false, destiladoConIngrediente: "", puntasVol: 22, puntasAlc: 65.5, mezcalVol: 185, mezcalAlc: 48.8, colasVol: 45, colasAlc: 18.0 },
        { id: 2, fechaSegunda: "2026-07-25", destiladorNum: "Alambique Cobre 01", volADestilar: 600, esDestiladoCon: false, destiladoConIngrediente: "", puntasVol: 24, puntasAlc: 66.0, mezcalVol: 190, mezcalAlc: 49.0, colasVol: 42, colasAlc: 17.5 },
        { id: 3, fechaSegunda: "2026-07-26", destiladorNum: "Alambique Cobre 02", volADestilar: 600, esDestiladoCon: false, destiladoConIngrediente: "", puntasVol: 23, puntasAlc: 65.0, mezcalVol: 188, mezcalAlc: 48.5, colasVol: 44, colasAlc: 18.2 }
      ],
      totalVolCargado: 1800,
      totalPuntas: 69,
      totalMezcalObtenido: 563,
      avgAlcMezcal: 48.8,
      totalColas: 131,
      observaciones: "Destilación lenta a fuego medio con separación rigurosa de cortes."
    },
    granel: {
      idContenedor: "Tanque Acero Inox T-04",
      categoria: "Mezcal Artesanal",
      clase: "Joven / Blanco",
      fechaMovimiento: "2026-07-28",
      noAnalisis: "ANA-2026-098",
      volInicial: 563,
      alcInicial: 48.8,
      volAguaAjuste: 18,
      volFinal: 581,
      alcFinal: 47.0,
      observaciones: "Ajuste alcohólico efectuado con agua desmineralizada de alta pureza."
    }
  },
  {
    id: "PROD-2026-002",
    noProduccion: "PROD-2026-002",
    unidadProduccion: "Destilería Los Magueyales (UP-019)",
    predio: "Loma Bonita - San Baltazar (Guía No. G-9120)",
    tipoMaguey: "Agave potatorum Zucc (Tobalá)",
    etapaActual: "destilacion",
    statusUI: "aprobado_ui",
    fechaInicio: "2026-07-25",
    fechaActualizacion: "2026-08-10",
    inspector: "Ing. Carlos Mendoza (UI Autorizado)",
    observacionesInspector: "Segunda destilación verificada satisfactoriamente. Listo para pasar a Granel.",
    coccion: {
      fechaIngreso: "2026-07-25",
      guiaPredio: "G-9120 / Registro Predio: PR-OAX-310",
      numPinas: 650,
      kgMagueyCrudo: 5200,
      pctArt: 26.0,
      noInforme: "INF-COC-2026-112",
      tipoHorno: "Cónico bajo tierra con piedras de río",
      fechaInicioCoccion: "2026-07-26",
      fechaFinCoccion: "2026-07-30",
      kgMagueyCocido: 4500,
      observaciones: "Cocción lenta con leña de mezquite."
    },
    molienda: {
      tipoMolienda: "Mazo de madera en canoa",
      fechaRegistro: "2026-08-01",
      items: [
        { id: 1, contenedor: "Tina Sabino 01", kgMaguey: 2250, fechaFermentacion: "2026-08-01", volumenFormulado: 1400, fechaPrimeraDestilacion: "2026-08-06", volumenOrdinario: 380, pctAlcOrdinario: 25.0, volumenColas: 45 },
        { id: 2, contenedor: "Tina Sabino 02", kgMaguey: 2250, fechaFermentacion: "2026-08-01", volumenFormulado: 1400, fechaPrimeraDestilacion: "2026-08-06", volumenOrdinario: 390, pctAlcOrdinario: 25.5, volumenColas: 50 }
      ],
      totalKgMaguey: 4500,
      totalVolFormulado: 2800,
      totalVolOrdinario: 770,
      observaciones: "Fermentación en tinas de madera de sabino."
    },
    destilacion: {
      tipoAlambique: "Olla de Barro Tradicional",
      fechaRegistro: "2026-08-08",
      items: [
        { id: 1, fechaSegunda: "2026-08-08", destiladorNum: "Olla de Barro 01", volADestilar: 385, esDestiladoCon: false, destiladoConIngrediente: "", puntasVol: 15, puntasAlc: 68.0, mezcalVol: 120, mezcalAlc: 51.5, colasVol: 30, colasAlc: 19.0 },
        { id: 2, fechaSegunda: "2026-08-09", destiladorNum: "Olla de Barro 02", volADestilar: 385, esDestiladoCon: false, destiladoConIngrediente: "", puntasVol: 16, puntasAlc: 67.5, mezcalVol: 125, mezcalAlc: 51.0, colasVol: 32, colasAlc: 18.5 }
      ],
      totalVolCargado: 770,
      totalPuntas: 31,
      totalMezcalObtenido: 245,
      avgAlcMezcal: 51.2,
      totalColas: 62,
      observaciones: "Destilación ancestral en ollas de barro con carrizo."
    }
  },
  {
    id: "PROD-2026-003",
    noProduccion: "PROD-2026-003",
    unidadProduccion: "Unidad de Producción San Juan del Río (UP-005)",
    predio: "La Cañada (Guía No. G-9430)",
    tipoMaguey: "Agave americana L. (Arroqueño)",
    etapaActual: "molienda",
    statusUI: "aprobado_ui",
    fechaInicio: "2026-08-05",
    fechaActualizacion: "2026-08-16",
    inspector: "Dra. Elena Ramos (UI Auditor)",
    observacionesInspector: "Molienda y fermentación validadas. Habilitado para registro de 2da Destilación.",
    coccion: {
      fechaIngreso: "2026-08-05",
      guiaPredio: "G-9430 / Registro Predio: PR-OAX-881",
      numPinas: 280,
      kgMagueyCrudo: 8800,
      pctArt: 23.8,
      noInforme: "INF-COC-2026-155",
      tipoHorno: "Cónico bajo tierra",
      fechaInicioCoccion: "2026-08-06",
      fechaFinCoccion: "2026-08-11",
      kgMagueyCocido: 7600,
      observaciones: "Piñas maduras de gran tamaño de 14 años de edad."
    },
    molienda: {
      tipoMolienda: "Desgarradora mecánica y tahona",
      fechaRegistro: "2026-08-13",
      items: [
        { id: 1, contenedor: "Tina 01", kgMaguey: 3800, fechaFermentacion: "2026-08-13", volumenFormulado: 2200, fechaPrimeraDestilacion: "2026-08-18", volumenOrdinario: 620, pctAlcOrdinario: 27.5, volumenColas: 75 },
        { id: 2, contenedor: "Tina 02", kgMaguey: 3800, fechaFermentacion: "2026-08-13", volumenFormulado: 2200, fechaPrimeraDestilacion: "2026-08-18", volumenOrdinario: 640, pctAlcOrdinario: 28.0, volumenColas: 80 }
      ],
      totalKgMaguey: 7600,
      totalVolFormulado: 4400,
      totalVolOrdinario: 1260,
      observaciones: "Fermentación controlada a temperatura de 24°C."
    }
  },
  {
    id: "PROD-2026-004",
    noProduccion: "PROD-2026-004",
    unidadProduccion: "Palenque El Manantial (UP-031)",
    predio: "Cerro del Maguey (Guía No. G-9602)",
    tipoMaguey: "Agave marmorata (Tepeztate)",
    etapaActual: "coccion",
    statusUI: "aprobado_ui",
    fechaInicio: "2026-08-15",
    fechaActualizacion: "2026-08-21",
    inspector: "Ing. Carlos Mendoza (UI Autorizado)",
    observacionesInspector: "Cocción aprobada. Se pesaron 6,200 kg de maguey cocido.",
    coccion: {
      fechaIngreso: "2026-08-15",
      guiaPredio: "G-9602 / Registro Predio: PR-OAX-902",
      numPinas: 310,
      kgMagueyCrudo: 7400,
      pctArt: 22.5,
      noInforme: "INF-COC-2026-178",
      tipoHorno: "Cónico tradicional de mampostería",
      fechaInicioCoccion: "2026-08-16",
      fechaFinCoccion: "2026-08-21",
      kgMagueyCocido: 6200,
      observaciones: "Agave silvestre Tepeztate con 20 años de maduración."
    }
  },
  {
    id: "PROD-2026-005",
    noProduccion: "PROD-2026-005",
    unidadProduccion: "Palenque Don Valente (UP-014)",
    predio: "La Nopalera (Guía No. G-9710)",
    tipoMaguey: "Agave angustifolia Haw (Espadín)",
    etapaActual: "coccion",
    statusUI: "pendiente_ui",
    fechaInicio: "2026-08-22",
    fechaActualizacion: "2026-08-22",
    inspector: "Pendiente de V.Bo. de Unidad de Inspección",
    observacionesInspector: "",
    coccion: {
      fechaIngreso: "2026-08-22",
      guiaPredio: "G-9710 / Registro Predio: PR-OAX-940",
      numPinas: 500,
      kgMagueyCrudo: 11000,
      pctArt: 25.2,
      noInforme: "INF-COC-2026-192",
      tipoHorno: "Cónico bajo tierra",
      fechaInicioCoccion: "2026-08-23",
      fechaFinCoccion: "2026-08-28",
      kgMagueyCocido: 9400,
      observaciones: "Entrada de agave de corte reciente con excelente concentración de azúcares."
    }
  }
];

// Registros de muestra para las secciones complementarias
const INITIAL_ELABORACION = [
  {
    id: "ELAB-01",
    fechaIngreso: "2026-07-27",
    loteOrigen: "PROD-2026-001",
    volumenOrigen: "495",
    pctAlcOrigen: "47.0",
    categoriaOrigen: "Mezcal Artesanal",
    claseOrigen: "Joven / Blanco",
    ingredientesOrigen: "Maguey Espadín",
    edadOrigen: "Sin edad",
    analisisOrigen: "ANA-2026-098",
    tipoMagueyOrigen: "Espadín",
    ingredientesAdicionados: "Hierbabuena y cáscara de naranja",
    procesoRealizado: "ABOCADO CON",
    descripcionProceso: "Abocado por infusión directa durante 24 horas",
    fechaSalida: "2026-07-28",
    loteSalida: "PROD-2026-001-ABO",
    volumenSalida: "490",
    pctAlcSalida: "46.5",
    claseSalida: "Abocado con",
    observaciones: "Estabilizado térmicamente antes de salida.",
    inspector: "Ing. Carlos Mendoza (28/07/2026)",
    status: "aprobado"
  }
];

const INITIAL_MADURACION = [
  {
    id: "MAD-01",
    fechaMovimiento: "2026-07-28",
    noLote: "PROD-2026-001",
    categoria: "Mezcal Artesanal",
    clase: "Reposado",
    ingredientes: "Maguey Espadín",
    edad: "0 meses",
    tipoMaguey: "Espadín",
    noAnalisis: "ANA-2026-098",
    tipoRecipiente: "BARRICAS DE MADERA DE",
    detalleRecipiente: "Roble Blanco Americano - Tostado Medio",
    invInicialFecha: "2026-07-28",
    invInicialRecipientes: "2",
    invInicialCapacidad: "200",
    invInicialVolTotal: "400",
    invInicialAlc: "47.0",
    status: "aprobado"
  }
];

const INITIAL_CONDICIONES = [
  {
    id: "COND-01",
    mes: "Julio",
    anio: "2026",
    fecha: "2026-07-28",
    temperaturaMin: "18",
    temperaturaMax: "26",
    humedadMin: "45",
    humedadMax: "65",
    observaciones: "Condiciones estables de almacenamiento dentro de norma.",
    inspector: "Ing. Carlos Mendoza (28/07/2026)",
    status: "aprobado",
    lecturas: [
      { id: 1, dia: 28, hora: "08:00", temp: "19", hum: "55" },
      { id: 2, dia: 28, hora: "14:00", temp: "25", hum: "48" }
    ]
  }
];

const INITIAL_QUEJAS = [
  {
    id: "QUEJA-01",
    fechaRecepcion: "2026-07-30",
    noLote: "PROD-2026-001",
    descripcionProducto: "Mezcal Artesanal - Joven / Blanco (Espadín)",
    tipoQueja: "Presentación / Sellado",
    nombreQuejoso: "Distribuidora Los Valles S.A. de C.V.",
    descripcionQueja: "Se reportó ligera fuga en tapón de muestra de lote.",
    accionesTomadas: "Se procedió al cambio de sello y revisión de lote.",
    estatusResolucion: "Resuelto",
    fechaResolucion: "2026-08-01",
    observaciones: "Queja atendida satisfactoriamente sin afectación a la calidad del mezcal.",
    status: "aprobado"
  }
];

// Configuración de Tabs
const TABS_CONFIG = [
  { id: 0, label: "Trazabilidad Integral", shortLabel: "Trazabilidad", icon: TimelineIcon },
  { id: 1, label: "Secc. II: Cocción", shortLabel: "Cocción", icon: LocalFireDepartmentIcon },
  { id: 2, label: "Secc. III (A): Molienda & Ferm.", shortLabel: "Molienda", icon: ScienceIcon },
  { id: 3, label: "Secc. III (B) & IV: Destilación", shortLabel: "Destilación", icon: WineBarIcon },
  { id: 4, label: "Secc. V: Granel", shortLabel: "Granel", icon: CheckCircleIcon },
  { id: 5, label: "Secc. VI: Clases & Reprocesos", shortLabel: "Clases", icon: LayersIcon },
  { id: 6, label: "Secc. VII: Maduración", shortLabel: "Maduración", icon: HourglassBottomIcon },
  { id: 7, label: "Secc. VIII: Condiciones", shortLabel: "Condiciones", icon: DeviceThermostatIcon },
  { id: 8, label: "Secc. IX: Quejas", shortLabel: "Quejas", icon: ReportProblemIcon }
];

const BitacoraProduccion = () => {
  // Pestaña activa: 0 (Trazabilidad) o 1-8 (Secciones individuales)
  const [activeTab, setActiveTab] = useState(0);

  // Obtener instalaciones registradas del cliente desde Redux con fallback enriquecido
  const reduxInstalaciones = useSelector(state =>
    state.FC_36Beneficiario?.instalacionesCliente ||
    state.FC_35Beneficiario?.instalacionesCliente ||
    state.Solicitudes?.instalacionesCliente ||
    state.Instalacion?.instalacionesCliente ||
    []
  );

  const availableInstalaciones = useMemo(() => {
    if (Array.isArray(reduxInstalaciones) && reduxInstalaciones.length > 0) {
      return reduxInstalaciones;
    }
    return DEFAULT_INSTALACIONES;
  }, [reduxInstalaciones]);

  // Estado maestro con todos los lotes y su trazabilidad
  const [productionLots, setProductionLots] = useState(INITIAL_LOTS);

  // Estados independientes de cada sección para compatibilidad total de tabs
  const [elaboracionRecords, setElaboracionRecords] = useState(INITIAL_ELABORACION);
  const [maduracionRecords, setMaduracionRecords] = useState(INITIAL_MADURACION);
  const [condicionesRecords, setCondicionesRecords] = useState(INITIAL_CONDICIONES);
  const [quejasRecords, setQuejasRecords] = useState(INITIAL_QUEJAS);

  // Estados de formularios de subcomponentes
  const [isSectionFormOpen, setIsSectionFormOpen] = useState(false);
  const [formSectionData, setFormSectionData] = useState({});

  // Filtros y Búsqueda para la tabla única de Trazabilidad
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStageFilter, setSelectedStageFilter] = useState("todas");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("todos");

  // Modales interactivos del flujo de Trazabilidad
  const [activeModal, setActiveModal] = useState(null); // 'coccion' | 'molienda' | 'destilacion' | 'granel' | 'trazabilidad' | 'dictamen'
  const [selectedLot, setSelectedLot] = useState(null);

  // Formulario Cocción
  const [formCoccion, setFormCoccion] = useState({
    noProduccion: "",
    idUnidadProduccion: "UP-042",
    unidadProduccion: "Palenque Tradicional Santiago Matatlán (UP-042)",
    predio: "",
    guiaPredio: "",
    tipoMaguey: MAGUEY_SPECIES[0],
    numPinas: "",
    kgMagueyCrudo: "",
    pctArt: "",
    noInforme: "",
    tipoHorno: "Cónico bajo tierra con leña de encino",
    fechaIngreso: new Date().toISOString().split('T')[0],
    fechaInicioCoccion: new Date().toISOString().split('T')[0],
    fechaFinCoccion: "",
    kgMagueyCocido: "",
    observaciones: ""
  });

  // Formulario Molienda
  const [formMolienda, setFormMolienda] = useState({
    tipoMolienda: "Tahona de cantera jalada por bestia",
    items: [
      {
        id: 1,
        contenedor: "Tina 01 (Madera de Sabino)",
        kgMaguey: "",
        fechaFermentacion: new Date().toISOString().split('T')[0],
        volumenFormulado: "",
        fechaPrimeraDestilacion: "",
        volumenOrdinario: "",
        pctAlcOrdinario: "",
        volumenColas: ""
      }
    ],
    observaciones: ""
  });

  // Formulario Destilación
  const [formDestilacion, setFormDestilacion] = useState({
    tipoAlambique: "Alambique de Cobre Tradicional",
    items: [
      {
        id: 1,
        fechaSegunda: new Date().toISOString().split('T')[0],
        destiladorNum: "Alambique Cobre 01",
        volADestilar: "",
        esDestiladoCon: false,
        destiladoConIngrediente: "",
        puntasVol: "",
        puntasAlc: "",
        mezcalVol: "",
        mezcalAlc: "",
        colasVol: "",
        colasAlc: ""
      }
    ],
    observaciones: ""
  });

  // Formulario Granel
  const [formGranel, setFormGranel] = useState({
    idContenedor: "Tanque Acero Inox T-01",
    categoria: "Mezcal Artesanal",
    clase: "Joven / Blanco",
    fechaMovimiento: new Date().toISOString().split('T')[0],
    noAnalisis: "",
    volInicial: "",
    alcInicial: "",
    volAguaAjuste: "0",
    volFinal: "",
    alcFinal: "",
    observaciones: ""
  });

  // Formulario Dictamen UI
  const [formDictamen, setFormDictamen] = useState({
    statusUI: "aprobado_ui",
    observacionesInspector: "",
    inspectorName: "Ing. Carlos Mendoza (UI Autorizado)"
  });

  // Filtrado y cómputo de la tabla de trazabilidad
  const filteredLots = useMemo(() => {
    return productionLots.filter(lot => {
      const term = searchTerm.toLowerCase();
      const matchSearch =
        lot.noProduccion.toLowerCase().includes(term) ||
        lot.tipoMaguey.toLowerCase().includes(term) ||
        lot.predio.toLowerCase().includes(term) ||
        (lot.inspector && lot.inspector.toLowerCase().includes(term));

      const matchStage = selectedStageFilter === "todas" || lot.etapaActual === selectedStageFilter;
      const matchStatus = selectedStatusFilter === "todos" || lot.statusUI === selectedStatusFilter;

      return matchSearch && matchStage && matchStatus;
    });
  }, [productionLots, searchTerm, selectedStageFilter, selectedStatusFilter]);

  // Contadores KPI
  const kpis = useMemo(() => {
    const total = productionLots.length;
    const coccion = productionLots.filter(l => l.etapaActual === "coccion").length;
    const molienda = productionLots.filter(l => l.etapaActual === "molienda").length;
    const destilacion = productionLots.filter(l => l.etapaActual === "destilacion").length;
    const granel = productionLots.filter(l => l.etapaActual === "granel").length;
    const pendientes = productionLots.filter(l => l.statusUI === "pendiente_ui").length;
    return { total, coccion, molienda, destilacion, granel, pendientes };
  }, [productionLots]);

  // Registros mapeados para subcomponentes individuales
  const coccionSectionRecords = useMemo(() => {
    return productionLots.filter(l => l.coccion).map(l => ({
      id: l.noProduccion,
      rawId: l.id,
      noProduccion: l.noProduccion,
      pinas: l.coccion.numPinas,
      kgMaguey: `${l.coccion.kgMagueyCrudo?.toLocaleString()} kg`,
      fechaIngreso: l.coccion.fechaIngreso,
      guiaPredio: l.coccion.guiaPredio,
      tipoMaguey: l.tipoMaguey,
      pctArt: `${l.coccion.pctArt}%`,
      noInforme: l.coccion.noInforme,
      fechaInicioCoccion: l.coccion.fechaInicioCoccion,
      fechaFinCoccion: l.coccion.fechaFinCoccion,
      kgMagueyCocido: `${l.coccion.kgMagueyCocido?.toLocaleString()} kg`,
      observaciones: l.coccion.observaciones,
      inspector: l.inspector,
      status: l.statusUI === "aprobado_ui" ? "aprobado" : l.statusUI === "rechazado" ? "rechazado" : "borrador"
    }));
  }, [productionLots]);

  const moliendaSectionRecords = useMemo(() => {
    return productionLots.filter(l => l.molienda).map(l => ({
      id: l.noProduccion,
      noProduccion: l.noProduccion,
      observaciones: l.molienda.observaciones,
      inspector: l.inspector,
      status: l.statusUI === "aprobado_ui" ? "aprobado" : l.statusUI === "rechazado" ? "rechazado" : "borrador",
      items: l.molienda.items.map(it => ({
        ...it,
        kgMaguey: `${it.kgMaguey} kg`,
        volumenFormulado: `${it.volumenFormulado} L`,
        volumenMezcal: `${it.volumenOrdinario} L`,
        pctAlcMezcal: `${it.pctAlcOrdinario}%`,
        volumenColas: `${it.volumenColas} L`,
        pctAlcColas: "18.0%"
      }))
    }));
  }, [productionLots]);

  const destilacionSectionRecords = useMemo(() => {
    return productionLots.filter(l => l.destilacion).map(l => ({
      id: l.noProduccion,
      noProduccion: l.noProduccion,
      fechaSegunda: l.destilacion.fechaRegistro,
      destiladorNum: l.destilacion.tipoAlambique,
      volADestilar: `${l.destilacion.totalVolCargado} L`,
      puntas: `${l.destilacion.totalPuntas} L`,
      mezcal: `${l.destilacion.totalMezcalObtenido} L (${l.destilacion.avgAlcMezcal}%)`,
      colas: `${l.destilacion.totalColas} L`,
      observaciones: l.destilacion.observaciones,
      inspector: l.inspector,
      status: l.statusUI === "aprobado_ui" ? "aprobado" : l.statusUI === "rechazado" ? "rechazado" : "borrador",
      items: l.destilacion.items
    }));
  }, [productionLots]);

  const granelSectionRecords = useMemo(() => {
    return productionLots.filter(l => l.granel).map(l => ({
      id: l.noProduccion,
      noLote: l.noProduccion,
      idContenedor: l.granel.idContenedor,
      categoria: l.granel.categoria,
      clase: l.granel.clase,
      fechaMovimiento: l.granel.fechaMovimiento,
      noAnalisisFisicoquimico: l.granel.noAnalisis,
      invInicialVol: `${l.granel.volInicial} L`,
      invInicialAlc: `${l.granel.alcInicial}%`,
      entradaVolAgua: `${l.granel.volAguaAjuste} L`,
      invFinalVol: `${l.granel.volFinal} L`,
      invFinalAlc: `${l.granel.alcFinal}%`,
      tipoMaguey: l.tipoMaguey,
      observaciones: l.granel.observaciones,
      inspector: l.inspector,
      status: l.statusUI === "aprobado_ui" ? "aprobado" : "borrador"
    }));
  }, [productionLots]);

  // ===================================
  // ACCIONES Y APERTURA DE MODALES
  // ===================================

  const handleOpenNuevaCoccion = () => {
    const nextNum = productionLots.length + 1;
    const today = new Date().toISOString().split('T')[0];
    const noProdSug = `PROD-2026-${String(nextNum).padStart(3, '0')}`;

    setFormCoccion({
      noProduccion: noProdSug,
      unidadProduccion: "Palenque Tradicional Santiago Matatlán (UP-042)",
      predio: "Paraje Las Palmas (Guía No. G-" + Math.floor(1000 + Math.random() * 9000) + ")",
      guiaPredio: "G-" + Math.floor(1000 + Math.random() * 9000) + " / Registro Predio: PR-OAX-" + Math.floor(100 + Math.random() * 900),
      tipoMaguey: MAGUEY_SPECIES[0],
      numPinas: "450",
      kgMagueyCrudo: "9000",
      pctArt: "24.0",
      noInforme: `INF-COC-2026-${Math.floor(100 + Math.random() * 900)}`,
      tipoHorno: "Cónico bajo tierra con leña de encino",
      fechaIngreso: today,
      fechaInicioCoccion: today,
      fechaFinCoccion: today,
      kgMagueyCocido: "7800",
      observaciones: "Ingreso de agave maduro en óptimas condiciones fitosanitarias."
    });
    setActiveModal("coccion");
  };

  const handleSaveCoccion = (statusOrEvent) => {
    if (statusOrEvent && statusOrEvent.preventDefault) statusOrEvent.preventDefault();

    // Determinar fuente de datos (CoccionSection modal vs Tab 0 modal)
    const isFromSection = formSectionData && (formSectionData.noProduccion || formSectionData.guiaPredio || formSectionData.kgMaguey);
    const src = isFromSection ? formSectionData : formCoccion;

    const noProd = (src.noProduccion || "").trim();
    if (!noProd) {
      Swal.fire("Atención", "El número de producción es obligatorio.", "warning");
      return;
    }

    const rawStatus = typeof statusOrEvent === 'string' ? statusOrEvent : (src.status || "borrador");
    const statusUI = rawStatus === "aprobado" ? "aprobado_ui" : (rawStatus === "revision" ? "pendiente_ui" : "borrador_ui");

    const kgCrudo = Number(String(src.kgMaguey != null ? src.kgMaguey : (src.kgMagueyCrudo || 0)).replace(/[^\d.]/g, '')) || 0;
    const kgCocido = Number(String(src.kgMagueyCocido || 0).replace(/[^\d.]/g, '')) || 0;
    const numPinas = Number(src.numPinas || src.pinas || 0) || 0;
    const pctArt = Number(String(src.pctArt || 0).replace(/[^\d.]/g, '')) || 0;

    const targetId = src.rawId || src.id || noProd;
    const existingIndex = productionLots.findIndex(l => l.id === targetId || l.noProduccion === noProd);

    if (existingIndex >= 0) {
      const existingLot = productionLots[existingIndex];
      const updatedLot = {
        ...existingLot,
        noProduccion: noProd,
        idUnidadProduccion: src.idUnidadProduccion || existingLot.idUnidadProduccion,
        unidadProduccion: src.unidadProduccion || existingLot.unidadProduccion,
        predio: src.predio || src.guiaPredio || existingLot.predio,
        tipoMaguey: src.tipoMaguey || existingLot.tipoMaguey,
        fechaActualizacion: new Date().toISOString().split('T')[0],
        statusUI: rawStatus === "revision" ? "pendiente_ui" : (rawStatus === "aprobado" ? "aprobado_ui" : existingLot.statusUI),
        coccion: {
          ...existingLot.coccion,
          fechaIngreso: src.fechaIngreso || existingLot.coccion?.fechaIngreso,
          guiaPredio: src.guiaPredio || existingLot.coccion?.guiaPredio,
          numPinas: numPinas,
          kgMagueyCrudo: kgCrudo,
          pctArt: pctArt,
          noInforme: src.noInforme || "",
          tipoHorno: src.tipoHorno || existingLot.coccion?.tipoHorno || "Cónico bajo tierra con leña de encino",
          fechaInicioCoccion: src.fechaInicioCoccion || existingLot.coccion?.fechaInicioCoccion,
          fechaFinCoccion: src.fechaFinCoccion || existingLot.coccion?.fechaFinCoccion,
          kgMagueyCocido: kgCocido,
          observaciones: src.observaciones || ""
        }
      };

      const newLots = [...productionLots];
      newLots[existingIndex] = updatedLot;
      setProductionLots(newLots);
    } else {
      const newLot = {
        id: noProd,
        noProduccion: noProd,
        idUnidadProduccion: src.idUnidadProduccion || "",
        unidadProduccion: src.unidadProduccion || "Palenque Tradicional Santiago Matatlán (UP-042)",
        predio: src.predio || src.guiaPredio || "Predio Registrado",
        tipoMaguey: src.tipoMaguey || (Array.isArray(MAGUEY_SPECIES) ? MAGUEY_SPECIES[0] : "Agave angustifolia Haw (Espadín)"),
        etapaActual: "coccion",
        statusUI: statusUI,
        fechaInicio: src.fechaIngreso || new Date().toISOString().split('T')[0],
        fechaActualizacion: src.fechaIngreso || new Date().toISOString().split('T')[0],
        inspector: "Pendiente de V.Bo. de Unidad de Inspección",
        observacionesInspector: "",
        coccion: {
          fechaIngreso: src.fechaIngreso || new Date().toISOString().split('T')[0],
          guiaPredio: src.guiaPredio || "",
          numPinas: numPinas,
          kgMagueyCrudo: kgCrudo,
          pctArt: pctArt,
          noInforme: src.noInforme || "",
          tipoHorno: src.tipoHorno || "Cónico bajo tierra con leña de encino",
          fechaInicioCoccion: src.fechaInicioCoccion || new Date().toISOString().split('T')[0],
          fechaFinCoccion: src.fechaFinCoccion || new Date().toISOString().split('T')[0],
          kgMagueyCocido: kgCocido,
          observaciones: src.observaciones || ""
        }
      };
      setProductionLots(prev => [newLot, ...prev]);
    }

    setActiveModal(null);
    setIsSectionFormOpen(false);
    setFormSectionData({});

    Swal.fire({
      title: "¡Cocción Guardada!",
      html: `El lote <b>${noProd}</b> ha sido guardado exitosamente en etapa de <b>Cocción</b>.`,
      icon: "success",
      confirmButtonColor: "#4A5D23"
    });
  };

  const handleOpenMolienda = (lot) => {
    setSelectedLot(lot);
    const today = new Date().toISOString().split('T')[0];
    const kgCocido = lot.coccion?.kgMagueyCocido || 8000;
    const halfKg = Math.round(kgCocido / 2);

    setFormMolienda({
      tipoMolienda: "Tahona de cantera jalada por bestia",
      items: [
        {
          id: 1,
          contenedor: "Tina Sabino 01",
          kgMaguey: halfKg,
          fechaFermentacion: today,
          volumenFormulado: Math.round(halfKg * 0.6),
          fechaPrimeraDestilacion: today,
          volumenOrdinario: Math.round(halfKg * 0.1),
          pctAlcOrdinario: 26.5,
          volumenColas: 45
        },
        {
          id: 2,
          contenedor: "Tina Sabino 02",
          kgMaguey: kgCocido - halfKg,
          fechaFermentacion: today,
          volumenFormulado: Math.round((kgCocido - halfKg) * 0.6),
          fechaPrimeraDestilacion: today,
          volumenOrdinario: Math.round((kgCocido - halfKg) * 0.1),
          pctAlcOrdinario: 26.0,
          volumenColas: 50
        }
      ],
      observaciones: "Molienda tradicional y formulación con agua de manantial."
    });
    setActiveModal("molienda");
  };

  const handleSaveMolienda = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!selectedLot) return;

    const totalKg = formMolienda.items.reduce((acc, item) => acc + (Number(item.kgMaguey) || 0), 0);
    const totalFormulado = formMolienda.items.reduce((acc, item) => acc + (Number(item.volumenFormulado) || 0), 0);
    const totalOrdinario = formMolienda.items.reduce((acc, item) => acc + (Number(item.volumenOrdinario) || 0), 0);

    const updatedLots = productionLots.map(l => {
      if (l.id === selectedLot.id) {
        return {
          ...l,
          etapaActual: "molienda",
          statusUI: "pendiente_ui",
          fechaActualizacion: new Date().toISOString().split('T')[0],
          inspector: "Pendiente de V.Bo. de Unidad de Inspección",
          molienda: {
            tipoMolienda: formMolienda.tipoMolienda,
            fechaRegistro: new Date().toISOString().split('T')[0],
            items: formMolienda.items,
            totalKgMaguey: totalKg,
            totalVolFormulado: totalFormulado,
            totalVolOrdinario: totalOrdinario,
            observaciones: formMolienda.observaciones
          }
        };
      }
      return l;
    });

    setProductionLots(updatedLots);
    setActiveModal(null);
    setIsSectionFormOpen(false);

    Swal.fire({
      title: "¡Molienda & Fermentación Registrada!",
      html: `El lote <b>${selectedLot.noProduccion}</b> ha avanzado a la etapa de <b>Molienda & Fermentación</b>.`,
      icon: "success",
      confirmButtonColor: "#4A5D23"
    });
  };

  const handleOpenDestilacion = (lot) => {
    setSelectedLot(lot);
    const today = new Date().toISOString().split('T')[0];
    const volOrdinario = lot.molienda?.totalVolOrdinario || 800;

    setFormDestilacion({
      tipoAlambique: "Alambique de Cobre Tradicional",
      items: [
        {
          id: 1,
          fechaSegunda: today,
          destiladorNum: "Alambique Cobre 01",
          volADestilar: volOrdinario,
          esDestiladoCon: false,
          destiladoConIngrediente: "",
          puntasVol: Math.round(volOrdinario * 0.04),
          puntasAlc: 66.0,
          mezcalVol: Math.round(volOrdinario * 0.31),
          mezcalAlc: 49.0,
          colasVol: Math.round(volOrdinario * 0.08),
          colasAlc: 18.0
        }
      ],
      observaciones: "Segunda destilación a fuego controlado para refinación de mezcal."
    });
    setActiveModal("destilacion");
  };

  const handleSaveDestilacion = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!selectedLot) return;

    const totalCargado = formDestilacion.items.reduce((acc, item) => acc + (Number(item.volADestilar) || 0), 0);
    const totalPuntas = formDestilacion.items.reduce((acc, item) => acc + (Number(item.puntasVol) || 0), 0);
    const totalMezcal = formDestilacion.items.reduce((acc, item) => acc + (Number(item.mezcalVol) || 0), 0);
    const totalColas = formDestilacion.items.reduce((acc, item) => acc + (Number(item.colasVol) || 0), 0);

    const avgAlc = formDestilacion.items.length > 0
      ? (formDestilacion.items.reduce((acc, item) => acc + (Number(item.mezcalAlc) || 0), 0) / formDestilacion.items.length).toFixed(1)
      : 48.0;

    const updatedLots = productionLots.map(l => {
      if (l.id === selectedLot.id) {
        return {
          ...l,
          etapaActual: "destilacion",
          statusUI: "pendiente_ui",
          fechaActualizacion: new Date().toISOString().split('T')[0],
          inspector: "Pendiente de V.Bo. de Unidad de Inspección",
          destilacion: {
            tipoAlambique: formDestilacion.tipoAlambique,
            fechaRegistro: new Date().toISOString().split('T')[0],
            items: formDestilacion.items,
            totalVolCargado: totalCargado,
            totalPuntas: totalPuntas,
            totalMezcalObtenido: totalMezcal,
            avgAlcMezcal: Number(avgAlc),
            totalColas: totalColas,
            observaciones: formDestilacion.observaciones
          }
        };
      }
      return l;
    });

    setProductionLots(updatedLots);
    setActiveModal(null);
    setIsSectionFormOpen(false);

    Swal.fire({
      title: "¡Destilación Registrada!",
      html: `El lote <b>${selectedLot.noProduccion}</b> ha avanzado a la etapa de <b>Destilación</b>. Se obtuvieron ${totalMezcal} L de Mezcal listo para validación.`,
      icon: "success",
      confirmButtonColor: "#4A5D23"
    });
  };

  const handleOpenGranel = (lot) => {
    setSelectedLot(lot);
    const today = new Date().toISOString().split('T')[0];
    const mezcalVol = lot.destilacion?.totalMezcalObtenido || 300;
    const mezcalAlc = lot.destilacion?.avgAlcMezcal || 49.0;

    setFormGranel({
      idContenedor: "Tanque Acero Inox T-0" + (Math.floor(Math.random() * 8) + 1),
      categoria: "Mezcal Artesanal",
      clase: "Joven / Blanco",
      fechaMovimiento: today,
      noAnalisis: `ANA-2026-${Math.floor(100 + Math.random() * 900)}`,
      volInicial: mezcalVol,
      alcInicial: mezcalAlc,
      volAguaAjuste: "12",
      volFinal: mezcalVol + 12,
      alcFinal: "47.0",
      observaciones: "Traspaso a inventario de mezcal a granel con ajuste alcohólico verificado."
    });
    setActiveModal("granel");
  };

  const handleSaveGranel = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!selectedLot) return;

    const updatedLots = productionLots.map(l => {
      if (l.id === selectedLot.id) {
        return {
          ...l,
          etapaActual: "granel",
          statusUI: "aprobado_ui",
          fechaActualizacion: new Date().toISOString().split('T')[0],
          inspector: "Ing. Carlos Mendoza (UI Autorizado)",
          observacionesInspector: "Lote cerrado e ingresado a inventario a Granel satisfactoriamente.",
          granel: {
            idContenedor: formGranel.idContenedor,
            categoria: formGranel.categoria,
            clase: formGranel.clase,
            fechaMovimiento: formGranel.fechaMovimiento,
            noAnalisis: formGranel.noAnalisis,
            volInicial: Number(formGranel.volInicial) || 0,
            alcInicial: Number(formGranel.alcInicial) || 0,
            volAguaAjuste: Number(formGranel.volAguaAjuste) || 0,
            volFinal: Number(formGranel.volFinal) || 0,
            alcFinal: Number(formGranel.alcFinal) || 0,
            observaciones: formGranel.observaciones
          }
        };
      }
      return l;
    });

    setProductionLots(updatedLots);
    setActiveModal(null);
    setIsSectionFormOpen(false);

    Swal.fire({
      title: "¡Lote Transferido a Granel!",
      html: `El lote <b>${selectedLot.noProduccion}</b> ha completado todo el ciclo de producción y se encuentra registrado en <b>Inventario a Granel</b>.`,
      icon: "success",
      confirmButtonColor: "#4A5D23"
    });
  };

  const handleOpenTrazabilidad = (lot) => {
    setSelectedLot(lot);
    setActiveModal("trazabilidad");
  };

  const handleOpenDictamen = (lot) => {
    setSelectedLot(lot);
    setFormDictamen({
      statusUI: lot.statusUI === "pendiente_ui" ? "aprobado_ui" : lot.statusUI,
      observacionesInspector: lot.observacionesInspector || "Verificación física y documental conforme a la NOM-070-SCFI-2016.",
      inspectorName: lot.inspector.includes("Pendiente") ? "Ing. Carlos Mendoza (UI Autorizado)" : lot.inspector
    });
    setActiveModal("dictamen");
  };

  const handleSaveDictamen = (e) => {
    e.preventDefault();
    if (!selectedLot) return;

    const isApproved = formDictamen.statusUI === "aprobado_ui";

    const updatedLots = productionLots.map(l => {
      if (l.id === selectedLot.id) {
        return {
          ...l,
          statusUI: formDictamen.statusUI,
          inspector: formDictamen.inspectorName,
          observacionesInspector: formDictamen.observacionesInspector,
          fechaActualizacion: new Date().toISOString().split('T')[0]
        };
      }
      return l;
    });

    setProductionLots(updatedLots);
    setActiveModal(null);

    Swal.fire({
      title: isApproved ? "¡Etapa Aprobada por UI!" : "Etapa Marcada con Observaciones",
      html: `El lote <b>${selectedLot.noProduccion}</b> ahora tiene estatus <b>${isApproved ? 'Aceptado por UI' : 'Rechazado / Con Correcciones'}</b> en la etapa de <b>${selectedLot.etapaActual.toUpperCase()}</b>.`,
      icon: isApproved ? "success" : "warning",
      confirmButtonColor: isApproved ? "#4A5D23" : "#D97706"
    });
  };

  const handleDeleteLot = (lot) => {
    Swal.fire({
      title: `¿Eliminar lote ${lot.noProduccion}?`,
      text: "Esta acción solo eliminará el registro de la vista actual.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar"
    }).then((res) => {
      if (res.isConfirmed) {
        setProductionLots(prev => prev.filter(l => l.id !== lot.id));
        Swal.fire("Eliminado", "El lote fue eliminado de la tabla.", "success");
      }
    });
  };

  // Handlers para las secciones adicionales (Clases, Maduración, Condiciones, Quejas)
  const handleSaveElaboracion = (record) => {
    setElaboracionRecords(prev => [record, ...prev]);
    setIsSectionFormOpen(false);
    Swal.fire("Éxito", "Registro de Elaboración de Clases guardado.", "success");
  };

  const handleSaveMaduracion = (record) => {
    setMaduracionRecords(prev => [record, ...prev]);
    setIsSectionFormOpen(false);
    Swal.fire("Éxito", "Registro de Maduración guardado.", "success");
  };

  const handleSaveCondiciones = (record) => {
    setCondicionesRecords(prev => [record, ...prev]);
    setIsSectionFormOpen(false);
    Swal.fire("Éxito", "Registro de Condiciones Ambientales guardado.", "success");
  };

  const handleSaveQueja = (record) => {
    setQuejasRecords(prev => [record, ...prev]);
    setIsSectionFormOpen(false);
    Swal.fire("Éxito", "Registro de Queja guardado.", "success");
  };

  // ===================================
  // HELPERS VISUALES PARA LA TABLA
  // ===================================

  const getStageBadge = (stage) => {
    switch (stage) {
      case "coccion":
        return (
          <span className="stage-pill pill-coccion">
            <LocalFireDepartmentIcon style={{ fontSize: 16 }} />
            Cocción (Secc. II)
          </span>
        );
      case "molienda":
        return (
          <span className="stage-pill pill-molienda">
            <ScienceIcon style={{ fontSize: 16 }} />
            Molienda & Fermentación
          </span>
        );
      case "destilacion":
        return (
          <span className="stage-pill pill-destilacion">
            <WineBarIcon style={{ fontSize: 16 }} />
            2da Destilación
          </span>
        );
      case "granel":
        return (
          <span className="stage-pill pill-granel">
            <CheckCircleIcon style={{ fontSize: 16 }} />
            Granel / Concluido
          </span>
        );
      default:
        return <span className="stage-pill">{stage}</span>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "aprobado_ui":
        return (
          <span className="status-pill pill-aprobado">
            <VerifiedIcon style={{ fontSize: 15 }} />
            Aceptado por UI
          </span>
        );
      case "pendiente_ui":
        return (
          <span className="status-pill pill-pendiente">
            <PendingActionsIcon style={{ fontSize: 15 }} />
            Pendiente Validación UI
          </span>
        );
      case "rechazado":
        return (
          <span className="status-pill pill-rechazado">
            <ReportProblemIcon style={{ fontSize: 15 }} />
            Con Observaciones
          </span>
        );
      default:
        return <span className="status-pill">{status}</span>;
    }
  };

  const getStageMetricSummary = (lot) => {
    switch (lot.etapaActual) {
      case "coccion":
        return (
          <div className="metric-cell">
            <div className="metric-main">
              <strong>{lot.coccion?.kgMagueyCocido?.toLocaleString() || 0} kg</strong>
              <span>cocidos</span>
            </div>
            <div className="metric-sub">
              {lot.coccion?.numPinas || 0} piñas • {lot.coccion?.pctArt || 0}% ART
            </div>
          </div>
        );
      case "molienda":
        return (
          <div className="metric-cell">
            <div className="metric-main">
              <strong>{lot.molienda?.totalVolOrdinario?.toLocaleString() || 0} L</strong>
              <span>1ra dest.</span>
            </div>
            <div className="metric-sub">
              {lot.molienda?.items?.length || 0} tinas • {lot.molienda?.totalVolFormulado?.toLocaleString() || 0} L form.
            </div>
          </div>
        );
      case "destilacion":
        return (
          <div className="metric-cell">
            <div className="metric-main">
              <strong style={{ color: "#7C3AED" }}>{lot.destilacion?.totalMezcalObtenido?.toLocaleString() || 0} L</strong>
              <span>Mezcal</span>
            </div>
            <div className="metric-sub">
              Grad. {lot.destilacion?.avgAlcMezcal || 0}% Alc. • {lot.destilacion?.items?.length || 0} alambiques
            </div>
          </div>
        );
      case "granel":
        return (
          <div className="metric-cell">
            <div className="metric-main">
              <strong style={{ color: "#059669" }}>{lot.granel?.volFinal?.toLocaleString() || 0} L</strong>
              <span>en {lot.granel?.idContenedor || 'Contenedor'}</span>
            </div>
            <div className="metric-sub">
              {lot.granel?.clase || 'Joven'} • {lot.granel?.alcFinal || 0}% Alc.
            </div>
          </div>
        );
      default:
        return <span>-</span>;
    }
  };

  return (
    <div className="trazabilidad-produccion-container">
      {/* Background glow effects */}
      <div className="glow-blob glow-1"></div>
      <div className="glow-blob glow-2"></div>

      {/* =========================================================
          ENCABEZADO PRINCIPAL DE LA BITÁCORA DE PRODUCCIÓN
      ========================================================= */}
      <header className="main-trace-header">
        <div className="header-branding">
          <div className="header-icon-box">
            <LayersIcon style={{ fontSize: 32 }} />
          </div>
          <div>
            <div className="header-tag">COMERCAM • CONTROL DE PRODUCCIÓN</div>
            <h1>Bitácora de Producción</h1>
            <p>Registro y trazabilidad continua: Cocción → Molienda & Fermentación → Destilación → Granel</p>
          </div>
        </div>

        <div className="header-actions">
          <button
            className="btn-primary-action"
            onClick={handleOpenNuevaCoccion}
            title="Registrar nuevo lote iniciando con la etapa de Cocción"
          >
            <AddIcon />
            <span>+ Registrar Cocción (Nuevo Lote)</span>
          </button>
        </div>
      </header>

      {/* =========================================================
          BARRA DE PESTAÑAS (TABS SEGMENTADOS COMPACTOS)
      ========================================================= */}
      <div className="tabs-nav-bar-container">
        <div className="segmented-pill-container">
          {TABS_CONFIG.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                className={`segmented-pill-btn ${isActive ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  setIsSectionFormOpen(false);
                }}
              >
                <Icon style={{ fontSize: 16 }} />
                <span>{tab.shortLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* =========================================================================
          CONTENIDO SEGÚN LA PESTAÑA SELECCIONADA
      ========================================================================= */}

      {/* ================= PESTAÑA 0: TRAZABILIDAD INTEGRAL (TABLA UNIFICADA) ================= */}
      {activeTab === 0 && (
        <div className="animate-fadeIn">
          {/* BARRA DE HERRAMIENTAS: BÚSQUEDA Y FILTROS */}
          <section className="control-bar-wrapper">
            <div className="search-box">
              <SearchIcon className="search-icon" />
              <input
                type="text"
                placeholder="Buscar por lote, especie de maguey, predio, inspector..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="clear-search-btn" onClick={() => setSearchTerm("")}>
                  <CloseIcon style={{ fontSize: 16 }} />
                </button>
              )}
            </div>

            <div className="filter-group">
              <div className="filter-select-wrapper">
                <FilterListIcon className="filter-icon" />
                <select
                  value={selectedStatusFilter}
                  onChange={(e) => setSelectedStatusFilter(e.target.value)}
                >
                  <option value="todos">Todos los Estatus</option>
                  <option value="pendiente_ui">Pendiente de Validación UI</option>
                  <option value="aprobado_ui">Aceptado por UI</option>
                  <option value="rechazado">Con Observaciones</option>
                </select>
              </div>

              {(searchTerm || selectedStageFilter !== "todas" || selectedStatusFilter !== "todos") && (
                <button
                  className="btn-reset-filters"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedStageFilter("todas");
                    setSelectedStatusFilter("todos");
                  }}
                >
                  Limpiar Filtros
                </button>
              )}
            </div>
          </section>

          {/* TABLA ÚNICA DE TRAZABILIDAD DE PRODUCCIÓN */}
          <section className="table-responsive-card">
            <table className="traceability-table">
              <thead>
                <tr>
                  <th>No. Lote / Producción</th>
                  <th>Etapa Actual</th>
                  <th>Materia Prima</th>
                  <th>Métricas Clave</th>
                  <th>Línea de Vida</th>
                  <th>Dictamen UI</th>
                  <th style={{ textAlign: "center" }}>Acciones de Trazabilidad</th>
                </tr>
              </thead>
              <tbody>
                {filteredLots.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-table-message">
                      <HelpOutlineIcon style={{ fontSize: 40, color: "#9CA3AF", marginBottom: 8 }} />
                      <p>No se encontraron registros de producción que coincidan con los filtros aplicados.</p>
                    </td>
                  </tr>
                ) : (
                  filteredLots.map((lot) => {
                    const isCoccion = lot.etapaActual === "coccion";
                    const isMolienda = lot.etapaActual === "molienda";
                    const isDestilacion = lot.etapaActual === "destilacion";
                    const isGranel = lot.etapaActual === "granel";
                    const isAprobado = lot.statusUI === "aprobado_ui";

                    const stepNumber = isCoccion ? 1 : isMolienda ? 2 : isDestilacion ? 3 : 4;
                    const progressPct = (stepNumber / 4) * 100;

                    return (
                      <tr key={lot.id} className="lot-row">
                        {/* Columna 1: Folio e Identificación */}
                        <td>
                          <div className="lot-identification">
                            <span className="lot-number">{lot.noProduccion}</span>
                            <span className="lot-predio" title={lot.predio}>
                              {lot.predio}
                            </span>
                            <span className="lot-date">Inicio: {lot.fechaInicio}</span>
                          </div>
                        </td>

                        {/* Columna 2: Etapa Actual */}
                        <td>
                          {getStageBadge(lot.etapaActual)}
                        </td>

                        {/* Columna 3: Materia Prima */}
                        <td>
                          <div className="maguey-cell">
                            <div className="maguey-name" title={lot.tipoMaguey}>
                              <GrassIcon style={{ fontSize: 15, color: "#4A5D23" }} />
                              <span>{lot.tipoMaguey}</span>
                            </div>
                            <div className="maguey-weight">
                              {lot.coccion?.kgMagueyCrudo ? `${lot.coccion.kgMagueyCrudo.toLocaleString()} kg iniciales` : "-"}
                            </div>
                          </div>
                        </td>

                        {/* Columna 4: Métricas Clave de la etapa */}
                        <td>
                          {getStageMetricSummary(lot)}
                        </td>

                        {/* Columna 5: Stepper Visual de Avance */}
                        <td>
                          <div className="lifecycle-stepper">
                            <div className="progress-bar-bg">
                              <div
                                className="progress-bar-fill"
                                style={{ width: `${progressPct}%` }}
                              ></div>
                            </div>
                            <div className="stepper-dots">
                              <span className={`step-dot ${stepNumber >= 1 ? 'completed' : ''}`} title="1. Cocción">🔥</span>
                              <span className={`step-dot ${stepNumber >= 2 ? 'completed' : ''}`} title="2. Molienda & Fermentación">🧪</span>
                              <span className={`step-dot ${stepNumber >= 3 ? 'completed' : ''}`} title="3. Destilación">⚗️</span>
                              <span className={`step-dot ${stepNumber >= 4 ? 'completed' : ''}`} title="4. Granel">🍷</span>
                            </div>
                            <span className="stepper-text">Paso {stepNumber} de 4</span>
                          </div>
                        </td>

                        {/* Columna 6: Estatus UI e Inspector */}
                        <td>
                          <div className="inspector-cell">
                            {getStatusBadge(lot.statusUI)}
                            <span className="inspector-name" title={lot.inspector}>
                              {lot.inspector}
                            </span>
                            {lot.observacionesInspector && (
                              <span className="inspector-note" title={lot.observacionesInspector}>
                                Obs: {lot.observacionesInspector}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Columna 7: Acciones Contextuales */}
                        <td>
                          <div className="action-buttons-cell">
                            {isCoccion && (
                              isAprobado ? (
                                <button
                                  className="btn-next-stage btn-stage-molienda"
                                  onClick={() => handleOpenMolienda(lot)}
                                  title="Registrar Molienda & Fermentación para este lote"
                                >
                                  <ScienceIcon style={{ fontSize: 16 }} />
                                  <span>+ Molienda</span>
                                </button>
                              ) : (
                                <span className="action-locked-hint" title="Requiere ser Aceptado por UI para habilitar la Molienda">
                                  <PendingActionsIcon style={{ fontSize: 14 }} /> Requiere V.Bo. UI
                                </span>
                              )
                            )}

                            {isMolienda && (
                              isAprobado ? (
                                <button
                                  className="btn-next-stage btn-stage-destilacion"
                                  onClick={() => handleOpenDestilacion(lot)}
                                  title="Registrar 2da Destilación para este lote"
                                >
                                  <WineBarIcon style={{ fontSize: 16 }} />
                                  <span>+ Destilación</span>
                                </button>
                              ) : (
                                <span className="action-locked-hint" title="Requiere ser Aceptado por UI para habilitar la Destilación">
                                  <PendingActionsIcon style={{ fontSize: 14 }} /> Requiere V.Bo. UI
                                </span>
                              )
                            )}

                            {isDestilacion && (
                              isAprobado ? (
                                <button
                                  className="btn-next-stage btn-stage-granel"
                                  onClick={() => handleOpenGranel(lot)}
                                  title="Pasar producto a Inventario a Granel"
                                >
                                  <Inventory2Icon style={{ fontSize: 16 }} />
                                  <span>+ Granel</span>
                                </button>
                              ) : (
                                <span className="action-locked-hint" title="Requiere ser Aceptado por UI para pasar a Granel">
                                  <PendingActionsIcon style={{ fontSize: 14 }} /> Requiere V.Bo. UI
                                </span>
                              )
                            )}

                            {isGranel && (
                              <span className="stage-completed-badge">
                                <VerifiedIcon style={{ fontSize: 16, color: "#10B981" }} />
                                <span>Concluido</span>
                              </span>
                            )}

                            <div className="secondary-actions-group">
                              <button
                                className="btn-icon-action btn-trace"
                                onClick={() => handleOpenTrazabilidad(lot)}
                                title="Ver Trazabilidad Completa y Auditoría"
                              >
                                <TimelineIcon style={{ fontSize: 18 }} />
                              </button>

                              <button
                                className="btn-icon-action btn-audit"
                                onClick={() => handleOpenDictamen(lot)}
                                title="Dictamen / Validación UI"
                              >
                                <FactCheckIcon style={{ fontSize: 18 }} />
                              </button>

                              <button
                                className="btn-icon-action btn-delete"
                                onClick={() => handleDeleteLot(lot)}
                                title="Eliminar lote de prueba"
                              >
                                <DeleteOutlineIcon style={{ fontSize: 18 }} />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </section>
        </div>
      )}

      {/* ================= PESTAÑA 1: SECCIÓN II - COCCIÓN ================= */}
      {activeTab === 1 && (
        <div className="animate-fadeIn section-tab-container">
          <CoccionSection
            coccionRecords={coccionSectionRecords}
            isFormOpen={isSectionFormOpen}
            setIsFormOpen={setIsSectionFormOpen}
            formData={formSectionData}
            setFormData={setFormSectionData}
            magueySpecies={MAGUEY_SPECIES}
            handleSaveCoccion={handleSaveCoccion}
            instalaciones={availableInstalaciones}
          />
        </div>
      )}

      {/* ================= PESTAÑA 2: SECCIÓN III (A) - MOLIENDA & FERMENTACIÓN ================= */}
      {activeTab === 2 && (
        <div className="animate-fadeIn section-tab-container">
          <MoliendaSection
            moliendaRecords={moliendaSectionRecords}
            coccionRecords={coccionSectionRecords}
            isFormOpen={isSectionFormOpen}
            setIsFormOpen={setIsSectionFormOpen}
            formData={formSectionData}
            setFormData={setFormSectionData}
            handleSaveMolienda={handleSaveMolienda}
          />
        </div>
      )}

      {/* ================= PESTAÑA 3: SECCIÓN III (B) & IV - DESTILACIÓN ================= */}
      {activeTab === 3 && (
        <div className="animate-fadeIn section-tab-container">
          <DestilacionSection
            destilacionRecords={destilacionSectionRecords}
            moliendaRecords={moliendaSectionRecords}
            isFormOpen={isSectionFormOpen}
            setIsFormOpen={setIsSectionFormOpen}
            formData={formSectionData}
            setFormData={setFormSectionData}
            handleSaveDestilacion={handleSaveDestilacion}
          />
        </div>
      )}

      {/* ================= PESTAÑA 4: SECCIÓN V - GRANEL ================= */}
      {activeTab === 4 && (
        <div className="animate-fadeIn section-tab-container">
          <GranelSection
            granelRecords={granelSectionRecords}
            destilacionRecords={destilacionSectionRecords}
            isFormOpen={isSectionFormOpen}
            setIsFormOpen={setIsSectionFormOpen}
            formData={formSectionData}
            setFormData={setFormSectionData}
            handleSaveGranel={handleSaveGranel}
          />
        </div>
      )}

      {/* ================= PESTAÑA 5: SECCIÓN VI - ELABORACIÓN CLASES / REPROCESOS ================= */}
      {activeTab === 5 && (
        <div className="animate-fadeIn section-tab-container">
          <ElaboracionSection
            elaboracionRecords={elaboracionRecords}
            granelRecords={granelSectionRecords}
            isFormOpen={isSectionFormOpen}
            setIsFormOpen={setIsSectionFormOpen}
            formData={formSectionData}
            setFormData={setFormSectionData}
            handleSaveElaboracion={handleSaveElaboracion}
          />
        </div>
      )}

      {/* ================= PESTAÑA 6: SECCIÓN VII - MADURACIÓN ================= */}
      {activeTab === 6 && (
        <div className="animate-fadeIn section-tab-container">
          <MaduracionSection
            maduracionRecords={maduracionRecords}
            destilacionRecords={destilacionSectionRecords}
            granelRecords={granelSectionRecords}
            isFormOpen={isSectionFormOpen}
            setIsFormOpen={setIsSectionFormOpen}
            formData={formSectionData}
            setFormData={setFormSectionData}
            handleSaveMaduracion={handleSaveMaduracion}
          />
        </div>
      )}

      {/* ================= PESTAÑA 7: SECCIÓN VIII - CONDICIONES ================= */}
      {activeTab === 7 && (
        <div className="animate-fadeIn section-tab-container">
          <CondicionesSection
            condicionesRecords={condicionesRecords}
            destilacionRecords={destilacionSectionRecords}
            isFormOpen={isSectionFormOpen}
            setIsFormOpen={setIsSectionFormOpen}
            formData={formSectionData}
            setFormData={setFormSectionData}
            handleSaveCondiciones={handleSaveCondiciones}
          />
        </div>
      )}

      {/* ================= PESTAÑA 8: SECCIÓN IX - QUEJAS ================= */}
      {activeTab === 8 && (
        <div className="animate-fadeIn section-tab-container">
          <QuejasSection
            quejasRecords={quejasRecords}
            granelRecords={granelSectionRecords}
            isFormOpen={isSectionFormOpen}
            setIsFormOpen={setIsSectionFormOpen}
            formData={formSectionData}
            setFormData={setFormSectionData}
            handleSaveQueja={handleSaveQueja}
          />
        </div>
      )}

      {/* =========================================================================
          MODAL 1: REGISTRO DE COCCIÓN (CREACIÓN DE NUEVO LOTE)
      ========================================================================= */}
      {activeModal === "coccion" && createPortal(
        <div className="modal-backdrop-custom">
          <div className="modal-dialog-custom modal-lg animate-scaleIn">
            <div className="modal-header-custom header-theme-coccion">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <LocalFireDepartmentIcon />
                </div>
                <div>
                  <h3>Sección II: Registro de Cocción e Ingreso de Agave</h3>
                  <p>Apertura de nuevo lote de producción de mezcal</p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setActiveModal(null)}>
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={handleSaveCoccion} className="modal-form-custom">
              <div className="form-grid-3">
                <div className="form-group-custom">
                  <label>No. de Producción (Lote) *</label>
                  <input
                    type="text"
                    required
                    value={formCoccion.noProduccion}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, noProduccion: e.target.value }))}
                    placeholder="Ej. PROD-2026-006"
                  />
                </div>

                <div className="form-group-custom">
                  <label>Especie de Maguey *</label>
                  <select
                    value={formCoccion.tipoMaguey}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, tipoMaguey: e.target.value }))}
                  >
                    {MAGUEY_SPECIES.map((spec, i) => (
                      <option key={i} value={spec}>{spec}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group-custom">
                  <label>Instalación / Unidad de Producción *</label>
                  <select
                    value={formCoccion.idUnidadProduccion || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      const found = availableInstalaciones.find(i => String(i.idanexocliente || i.id) === String(val));
                      const alias = found?.informaciongeneralinstalacioncliente?.alias || found?.alias || val;
                      setFormCoccion(prev => ({
                        ...prev,
                        idUnidadProduccion: val,
                        unidadProduccion: alias
                      }));
                    }}
                    required
                  >
                    <option value="">-- Seleccionar Instalación --</option>
                    {availableInstalaciones.map((inst) => {
                      const idAnexo = inst.idanexocliente || inst.id;
                      const infoGen = inst.informaciongeneralinstalacioncliente || inst;
                      const alias = infoGen?.alias || inst.alias || `Instalación #${idAnexo}`;
                      return (
                        <option key={idAnexo} value={idAnexo}>
                          {alias}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group-custom">
                  <label>Nombre del Predio de Procedencia *</label>
                  <input
                    type="text"
                    required
                    value={formCoccion.predio}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, predio: e.target.value }))}
                    placeholder="Ej. Paraje El Sauz"
                  />
                </div>

                <div className="form-group-custom">
                  <label>Folio Guía de Maguey / Registro de Predio *</label>
                  <input
                    type="text"
                    required
                    value={formCoccion.guiaPredio}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, guiaPredio: e.target.value }))}
                    placeholder="Ej. G-9921 / PR-OAX-201"
                  />
                </div>
              </div>

              <div className="form-grid-4">
                <div className="form-group-custom">
                  <label>No. de Piñas</label>
                  <input
                    type="number"
                    value={formCoccion.numPinas}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, numPinas: e.target.value }))}
                    placeholder="450"
                  />
                </div>

                <div className="form-group-custom">
                  <label>Kg Maguey Crudo *</label>
                  <input
                    type="number"
                    required
                    value={formCoccion.kgMagueyCrudo}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, kgMagueyCrudo: e.target.value }))}
                    placeholder="9000"
                  />
                </div>

                <div className="form-group-custom">
                  <label>% Azúcares (ART)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formCoccion.pctArt}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, pctArt: e.target.value }))}
                    placeholder="24.5"
                  />
                </div>

                <div className="form-group-custom">
                  <label>No. Informe Muestreo</label>
                  <input
                    type="text"
                    value={formCoccion.noInforme}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, noInforme: e.target.value }))}
                    placeholder="INF-2026-001"
                  />
                </div>
              </div>

              <div className="form-grid-3">
                <div className="form-group-custom">
                  <label>Tipo de Horno</label>
                  <input
                    type="text"
                    value={formCoccion.tipoHorno}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, tipoHorno: e.target.value }))}
                  />
                </div>

                <div className="form-group-custom">
                  <label>Fecha Inicio Cocción</label>
                  <input
                    type="date"
                    value={formCoccion.fechaInicioCoccion}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, fechaInicioCoccion: e.target.value }))}
                  />
                </div>

                <div className="form-group-custom">
                  <label>Fecha Fin Cocción</label>
                  <input
                    type="date"
                    value={formCoccion.fechaFinCoccion}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, fechaFinCoccion: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group-custom">
                  <label>Kg Maguey Cocido (Obtenido/Pesado) *</label>
                  <input
                    type="number"
                    required
                    value={formCoccion.kgMagueyCocido}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, kgMagueyCocido: e.target.value }))}
                    placeholder="7800"
                  />
                </div>

                <div className="form-group-custom">
                  <label>Observaciones del Proceso</label>
                  <input
                    type="text"
                    value={formCoccion.observaciones}
                    onChange={(e) => setFormCoccion(prev => ({ ...prev, observaciones: e.target.value }))}
                    placeholder="Leña utilizada, tiempo de enfriamiento, etc."
                  />
                </div>
              </div>

              <div className="modal-footer-custom">
                <button type="button" className="btn-cancel-modal" onClick={() => setActiveModal(null)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-submit-modal btn-coccion-submit">
                  <LocalFireDepartmentIcon style={{ fontSize: 18 }} />
                  Guardar y Registrar Cocción
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          MODAL 2: REGISTRO DE MOLIENDA & FERMENTACIÓN
      ========================================================================= */}
      {activeModal === "molienda" && selectedLot && createPortal(
        <div className="modal-backdrop-custom">
          <div className="modal-dialog-custom modal-xl animate-scaleIn">
            <div className="modal-header-custom header-theme-molienda">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <ScienceIcon />
                </div>
                <div>
                  <h3>Sección III (A): Registro de Molienda, Fermentación & 1ra Destilación</h3>
                  <p>Lote: <strong>{selectedLot.noProduccion}</strong> • {selectedLot.tipoMaguey}</p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setActiveModal(null)}>
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={handleSaveMolienda} className="modal-form-custom">
              <div className="info-callout">
                <div className="callout-item">
                  <span>Materia Prima Disponible:</span>
                  <strong>{selectedLot.coccion?.kgMagueyCocido?.toLocaleString() || 0} kg de Maguey Cocido</strong>
                </div>
                <div className="callout-item">
                  <span>Predio de Origen:</span>
                  <strong>{selectedLot.predio}</strong>
                </div>
              </div>

              <div className="form-group-custom" style={{ marginTop: 12 }}>
                <label>Tipo de Molienda Utilizada</label>
                <input
                  type="text"
                  value={formMolienda.tipoMolienda}
                  onChange={(e) => setFormMolienda(prev => ({ ...prev, tipoMolienda: e.target.value }))}
                />
              </div>

              <div className="subtable-header">
                <h4>Monitoreo por Tina de Fermentación</h4>
                <button
                  type="button"
                  className="btn-add-row"
                  onClick={() => {
                    const today = new Date().toISOString().split('T')[0];
                    const nextId = formMolienda.items.length + 1;
                    setFormMolienda(prev => ({
                      ...prev,
                      items: [
                        ...prev.items,
                        {
                          id: Date.now(),
                          contenedor: `Tina 0${nextId} (Madera de Sabino)`,
                          kgMaguey: "",
                          fechaFermentacion: today,
                          volumenFormulado: "",
                          fechaPrimeraDestilacion: today,
                          volumenOrdinario: "",
                          pctAlcOrdinario: "",
                          volumenColas: ""
                        }
                      ]
                    }));
                  }}
                >
                  <AddIcon style={{ fontSize: 16 }} /> + Agregar Tina
                </button>
              </div>

              <div className="table-responsive-sub">
                <table className="sub-table">
                  <thead>
                    <tr>
                      <th>Contenedor / Tina</th>
                      <th>Kg Maguey</th>
                      <th>Fecha Ferm.</th>
                      <th>Vol. Formulado (L)</th>
                      <th>Fecha 1ra Dest.</th>
                      <th>Vol. Ordinario (L)</th>
                      <th>% Alc. Ordinario</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formMolienda.items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <input
                            type="text"
                            value={item.contenedor}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormMolienda(prev => ({
                                ...prev,
                                items: prev.items.map(it => it.id === item.id ? { ...it, contenedor: val } : it)
                              }));
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            required
                            placeholder="Kg"
                            value={item.kgMaguey}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormMolienda(prev => ({
                                ...prev,
                                items: prev.items.map(it => it.id === item.id ? { ...it, kgMaguey: val } : it)
                              }));
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            value={item.fechaFermentacion}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormMolienda(prev => ({
                                ...prev,
                                items: prev.items.map(it => it.id === item.id ? { ...it, fechaFermentacion: val } : it)
                              }));
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            placeholder="Litros"
                            value={item.volumenFormulado}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormMolienda(prev => ({
                                ...prev,
                                items: prev.items.map(it => it.id === item.id ? { ...it, volumenFormulado: val } : it)
                              }));
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            value={item.fechaPrimeraDestilacion}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormMolienda(prev => ({
                                ...prev,
                                items: prev.items.map(it => it.id === item.id ? { ...it, fechaPrimeraDestilacion: val } : it)
                              }));
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            required
                            placeholder="Ordinario (L)"
                            value={item.volumenOrdinario}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormMolienda(prev => ({
                                ...prev,
                                items: prev.items.map(it => it.id === item.id ? { ...it, volumenOrdinario: val } : it)
                              }));
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.1"
                            placeholder="% Alc"
                            value={item.pctAlcOrdinario}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormMolienda(prev => ({
                                ...prev,
                                items: prev.items.map(it => it.id === item.id ? { ...it, pctAlcOrdinario: val } : it)
                              }));
                            }}
                          />
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {formMolienda.items.length > 1 && (
                            <button
                              type="button"
                              className="btn-delete-row"
                              onClick={() => {
                                setFormMolienda(prev => ({
                                  ...prev,
                                  items: prev.items.filter(it => it.id !== item.id)
                                }));
                              }}
                            >
                              <DeleteOutlineIcon style={{ fontSize: 16 }} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="form-group-custom" style={{ marginTop: 16 }}>
                <label>Observaciones de Fermentación y 1ra Destilación</label>
                <input
                  type="text"
                  value={formMolienda.observaciones}
                  onChange={(e) => setFormMolienda(prev => ({ ...prev, observaciones: e.target.value }))}
                  placeholder="Levaduras, temperatura ambiental, tiempo de fermentación..."
                />
              </div>

              <div className="modal-footer-custom">
                <button type="button" className="btn-cancel-modal" onClick={() => setActiveModal(null)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-submit-modal btn-molienda-submit">
                  <ScienceIcon style={{ fontSize: 18 }} />
                  Guardar y Avanzar a Molienda
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          MODAL 3: REGISTRO DE 2DA DESTILACIÓN
      ========================================================================= */}
      {activeModal === "destilacion" && selectedLot && createPortal(
        <div className="modal-backdrop-custom">
          <div className="modal-dialog-custom modal-xl animate-scaleIn">
            <div className="modal-header-custom header-theme-destilacion">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <WineBarIcon />
                </div>
                <div>
                  <h3>Sección III (B) & IV: Registro de 2da Destilación (Refinado de Cortes)</h3>
                  <p>Lote: <strong>{selectedLot.noProduccion}</strong> • {selectedLot.tipoMaguey}</p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setActiveModal(null)}>
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={handleSaveDestilacion} className="modal-form-custom">
              <div className="info-callout">
                <div className="callout-item">
                  <span>Volumen de Ordinario de Entrada:</span>
                  <strong>{selectedLot.molienda?.totalVolOrdinario?.toLocaleString() || 0} Litros de 1ra Destilación</strong>
                </div>
                <div className="callout-item">
                  <span>Alambique Seleccionado:</span>
                  <strong>{formDestilacion.tipoAlambique}</strong>
                </div>
              </div>

              <div className="form-group-custom" style={{ marginTop: 12 }}>
                <label>Tipo de Alambique / Destilador</label>
                <input
                  type="text"
                  value={formDestilacion.tipoAlambique}
                  onChange={(e) => setFormDestilacion(prev => ({ ...prev, tipoAlambique: e.target.value }))}
                />
              </div>

              <div className="subtable-header">
                <h4>Detalle de Cortes por Alambique (2da Destilación)</h4>
                <button
                  type="button"
                  className="btn-add-row"
                  onClick={() => {
                    const today = new Date().toISOString().split('T')[0];
                    const nextId = formDestilacion.items.length + 1;
                    setFormDestilacion(prev => ({
                      ...prev,
                      items: [
                        ...prev.items,
                        {
                          id: Date.now(),
                          fechaSegunda: today,
                          destiladorNum: `Alambique Cobre 0${nextId}`,
                          volADestilar: "",
                          esDestiladoCon: false,
                          destiladoConIngrediente: "",
                          puntasVol: "",
                          puntasAlc: "",
                          mezcalVol: "",
                          mezcalAlc: "",
                          colasVol: "",
                          colasAlc: ""
                        }
                      ]
                    }));
                  }}
                >
                  <AddIcon style={{ fontSize: 16 }} /> + Agregar Alambique
                </button>
              </div>

              <div className="table-responsive-sub">
                <table className="sub-table">
                  <thead>
                    <tr>
                      <th>Identificador Alambique</th>
                      <th>Fecha 2da Dest.</th>
                      <th>Carga (L)</th>
                      <th>Puntas (L / %)</th>
                      <th>Mezcal Corazón (L / % Alc) *</th>
                      <th>Colas (L / %)</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formDestilacion.items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <input
                            type="text"
                            value={item.destiladorNum}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormDestilacion(prev => ({
                                ...prev,
                                items: prev.items.map(it => it.id === item.id ? { ...it, destiladorNum: val } : it)
                              }));
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            value={item.fechaSegunda}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormDestilacion(prev => ({
                                ...prev,
                                items: prev.items.map(it => it.id === item.id ? { ...it, fechaSegunda: val } : it)
                              }));
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            placeholder="Carga (L)"
                            value={item.volADestilar}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormDestilacion(prev => ({
                                ...prev,
                                items: prev.items.map(it => it.id === item.id ? { ...it, volADestilar: val } : it)
                              }));
                            }}
                          />
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4 }}>
                            <input
                              type="number"
                              placeholder="L"
                              style={{ width: "60px" }}
                              value={item.puntasVol}
                              onChange={(e) => {
                                const val = e.target.value;
                                setFormDestilacion(prev => ({
                                  ...prev,
                                  items: prev.items.map(it => it.id === item.id ? { ...it, puntasVol: val } : it)
                                }));
                              }}
                            />
                            <input
                              type="number"
                              step="0.1"
                              placeholder="%"
                              style={{ width: "60px" }}
                              value={item.puntasAlc}
                              onChange={(e) => {
                                const val = e.target.value;
                                setFormDestilacion(prev => ({
                                  ...prev,
                                  items: prev.items.map(it => it.id === item.id ? { ...it, puntasAlc: val } : it)
                                }));
                              }}
                            />
                          </div>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4 }}>
                            <input
                              type="number"
                              required
                              placeholder="Mezcal (L)"
                              style={{ width: "90px", fontWeight: "bold", color: "#7C3AED" }}
                              value={item.mezcalVol}
                              onChange={(e) => {
                                const val = e.target.value;
                                setFormDestilacion(prev => ({
                                  ...prev,
                                  items: prev.items.map(it => it.id === item.id ? { ...it, mezcalVol: val } : it)
                                }));
                              }}
                            />
                            <input
                              type="number"
                              step="0.1"
                              placeholder="% Alc"
                              style={{ width: "70px", fontWeight: "bold" }}
                              value={item.mezcalAlc}
                              onChange={(e) => {
                                const val = e.target.value;
                                setFormDestilacion(prev => ({
                                  ...prev,
                                  items: prev.items.map(it => it.id === item.id ? { ...it, mezcalAlc: val } : it)
                                }));
                              }}
                            />
                          </div>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4 }}>
                            <input
                              type="number"
                              placeholder="L"
                              style={{ width: "60px" }}
                              value={item.colasVol}
                              onChange={(e) => {
                                const val = e.target.value;
                                setFormDestilacion(prev => ({
                                  ...prev,
                                  items: prev.items.map(it => it.id === item.id ? { ...it, colasVol: val } : it)
                                }));
                              }}
                            />
                            <input
                              type="number"
                              step="0.1"
                              placeholder="%"
                              style={{ width: "60px" }}
                              value={item.colasAlc}
                              onChange={(e) => {
                                const val = e.target.value;
                                setFormDestilacion(prev => ({
                                  ...prev,
                                  items: prev.items.map(it => it.id === item.id ? { ...it, colasAlc: val } : it)
                                }));
                              }}
                            />
                          </div>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {formDestilacion.items.length > 1 && (
                            <button
                              type="button"
                              className="btn-delete-row"
                              onClick={() => {
                                setFormDestilacion(prev => ({
                                  ...prev,
                                  items: prev.items.filter(it => it.id !== item.id)
                                }));
                              }}
                            >
                              <DeleteOutlineIcon style={{ fontSize: 16 }} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="form-group-custom" style={{ marginTop: 16 }}>
                <label>Observaciones de Destilación</label>
                <input
                  type="text"
                  value={formDestilacion.observaciones}
                  onChange={(e) => setFormDestilacion(prev => ({ ...prev, observaciones: e.target.value }))}
                  placeholder="Intensidad de fuego, corte exacto de perla/mezcal, etc."
                />
              </div>

              <div className="modal-footer-custom">
                <button type="button" className="btn-cancel-modal" onClick={() => setActiveModal(null)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-submit-modal btn-destilacion-submit">
                  <WineBarIcon style={{ fontSize: 18 }} />
                  Guardar y Avanzar a Destilación
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          MODAL 4: REGISTRO DE GRANEL (CIERRE DE PRODUCCIÓN)
      ========================================================================= */}
      {activeModal === "granel" && selectedLot && createPortal(
        <div className="modal-backdrop-custom">
          <div className="modal-dialog-custom modal-lg animate-scaleIn">
            <div className="modal-header-custom header-theme-granel">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <Inventory2Icon />
                </div>
                <div>
                  <h3>Sección V: Cierre de Producción e Ingreso a Granel</h3>
                  <p>Lote: <strong>{selectedLot.noProduccion}</strong> • {selectedLot.tipoMaguey}</p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setActiveModal(null)}>
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={handleSaveGranel} className="modal-form-custom">
              <div className="info-callout">
                <div className="callout-item">
                  <span>Mezcal Obtenido en Destilación:</span>
                  <strong>{selectedLot.destilacion?.totalMezcalObtenido?.toLocaleString() || 0} Litros @ {selectedLot.destilacion?.avgAlcMezcal || 0}% Alc.</strong>
                </div>
              </div>

              <div className="form-grid-3">
                <div className="form-group-custom">
                  <label>ID Contenedor Destino *</label>
                  <input
                    type="text"
                    required
                    value={formGranel.idContenedor}
                    onChange={(e) => setFormGranel(prev => ({ ...prev, idContenedor: e.target.value }))}
                    placeholder="Ej. Tanque Acero Inox T-04"
                  />
                </div>

                <div className="form-group-custom">
                  <label>Categoría</label>
                  <select
                    value={formGranel.categoria}
                    onChange={(e) => setFormGranel(prev => ({ ...prev, categoria: e.target.value }))}
                  >
                    <option value="Mezcal Artesanal">Mezcal Artesanal</option>
                    <option value="Mezcal Ancestral">Mezcal Ancestral</option>
                    <option value="Mezcal">Mezcal</option>
                  </select>
                </div>

                <div className="form-group-custom">
                  <label>Clase</label>
                  <select
                    value={formGranel.clase}
                    onChange={(e) => setFormGranel(prev => ({ ...prev, clase: e.target.value }))}
                  >
                    <option value="Joven / Blanco">Joven / Blanco</option>
                    <option value="Reposado">Reposado</option>
                    <option value="Añejo">Añejo</option>
                    <option value="Abocado con">Abocado con</option>
                    <option value="Destilado con">Destilado con</option>
                  </select>
                </div>
              </div>

              <div className="form-grid-3">
                <div className="form-group-custom">
                  <label>No. Análisis Fisicoquímico</label>
                  <input
                    type="text"
                    value={formGranel.noAnalisis}
                    onChange={(e) => setFormGranel(prev => ({ ...prev, noAnalisis: e.target.value }))}
                    placeholder="ANA-2026-098"
                  />
                </div>

                <div className="form-group-custom">
                  <label>Agua Agregada para Ajuste (L)</label>
                  <input
                    type="number"
                    value={formGranel.volAguaAjuste}
                    onChange={(e) => {
                      const agua = Number(e.target.value) || 0;
                      const inicial = Number(formGranel.volInicial) || 0;
                      setFormGranel(prev => ({
                        ...prev,
                        volAguaAjuste: e.target.value,
                        volFinal: inicial + agua
                      }));
                    }}
                  />
                </div>

                <div className="form-group-custom">
                  <label>Volumen Final a Granel (L) *</label>
                  <input
                    type="number"
                    required
                    value={formGranel.volFinal}
                    onChange={(e) => setFormGranel(prev => ({ ...prev, volFinal: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group-custom">
                  <label>% Alc. Final Ajustado *</label>
                  <input
                    type="number"
                    step="0.1"
                    required
                    value={formGranel.alcFinal}
                    onChange={(e) => setFormGranel(prev => ({ ...prev, alcFinal: e.target.value }))}
                    placeholder="47.0"
                  />
                </div>

                <div className="form-group-custom">
                  <label>Observaciones de Almacén</label>
                  <input
                    type="text"
                    value={formGranel.observaciones}
                    onChange={(e) => setFormGranel(prev => ({ ...prev, observaciones: e.target.value }))}
                    placeholder="Condiciones de almacenamiento a granel..."
                  />
                </div>
              </div>

              <div className="modal-footer-custom">
                <button type="button" className="btn-cancel-modal" onClick={() => setActiveModal(null)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-submit-modal btn-granel-submit">
                  <CheckCircleIcon style={{ fontSize: 18 }} />
                  Cerrar Producción y Pasar a Granel
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          MODAL 5: TRAZABILIDAD COMPLETA / AUDITORÍA INTEGRAL
      ========================================================================= */}
      {activeModal === "trazabilidad" && selectedLot && createPortal(
        <div className="modal-backdrop-custom">
          <div className="modal-dialog-custom modal-xl animate-scaleIn">
            <div className="modal-header-custom header-theme-trazabilidad">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <TimelineIcon />
                </div>
                <div>
                  <h3>Expediente de Trazabilidad Integral</h3>
                  <p>Lote: <strong>{selectedLot.noProduccion}</strong> • {selectedLot.tipoMaguey}</p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setActiveModal(null)}>
                <CloseIcon />
              </button>
            </div>

            <div className="modal-body-scrollable">
              <div className="summary-banner-audit">
                <div className="audit-kpi">
                  <span className="label">Unidad de Producción:</span>
                  <strong>{selectedLot.unidadProduccion}</strong>
                </div>
                <div className="audit-kpi">
                  <span className="label">Predio & Guía:</span>
                  <strong>{selectedLot.predio}</strong>
                </div>
                <div className="audit-kpi">
                  <span className="label">Estatus Actual:</span>
                  <div>{getStatusBadge(selectedLot.statusUI)}</div>
                </div>
                <div className="audit-kpi">
                  <span className="label">Inspector Asignado:</span>
                  <strong>{selectedLot.inspector}</strong>
                </div>
              </div>

              <div className="audit-timeline">
                {/* FASE 1: COCCIÓN */}
                <div className={`timeline-card ${selectedLot.coccion ? 'active-step' : ''}`}>
                  <div className="timeline-node">
                    <LocalFireDepartmentIcon />
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-head">
                      <h4>1. Sección II: Cocción de Agave</h4>
                      <span className="badge-step-done">Completada</span>
                    </div>
                    {selectedLot.coccion && (
                      <div className="timeline-grid-3">
                        <div><strong>Ingreso:</strong> {selectedLot.coccion.fechaIngreso}</div>
                        <div><strong>Piñas:</strong> {selectedLot.coccion.numPinas}</div>
                        <div><strong>Kg Crudo:</strong> {selectedLot.coccion.kgMagueyCrudo?.toLocaleString()} kg</div>
                        <div><strong>Kg Cocido:</strong> {selectedLot.coccion.kgMagueyCocido?.toLocaleString()} kg</div>
                        <div><strong>% ART:</strong> {selectedLot.coccion.pctArt}%</div>
                        <div><strong>Horno:</strong> {selectedLot.coccion.tipoHorno}</div>
                        <div style={{ gridColumn: "span 3" }}><strong>Observaciones:</strong> {selectedLot.coccion.observaciones}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* FASE 2: MOLIENDA & FERMENTACIÓN */}
                <div className={`timeline-card ${selectedLot.molienda ? 'active-step' : 'pending-step'}`}>
                  <div className="timeline-node">
                    <ScienceIcon />
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-head">
                      <h4>2. Sección III (A): Molienda, Fermentación & 1ra Destilación</h4>
                      {selectedLot.molienda ? (
                        <span className="badge-step-done">Completada</span>
                      ) : (
                        <span className="badge-step-pending">En espera</span>
                      )}
                    </div>
                    {selectedLot.molienda ? (
                      <div>
                        <div className="timeline-grid-3">
                          <div><strong>Tipo Molienda:</strong> {selectedLot.molienda.tipoMolienda}</div>
                          <div><strong>Kg Procesados:</strong> {selectedLot.molienda.totalKgMaguey?.toLocaleString()} kg</div>
                          <div><strong>Vol. Formulado:</strong> {selectedLot.molienda.totalVolFormulado?.toLocaleString()} L</div>
                          <div><strong>Vol. Ordinario (1ra Dest):</strong> <span style={{ color: "#4A5D23", fontWeight: "bold" }}>{selectedLot.molienda.totalVolOrdinario?.toLocaleString()} L</span></div>
                          <div><strong>No. Tinas:</strong> {selectedLot.molienda.items?.length || 0}</div>
                        </div>

                        <div className="timeline-sublist">
                          <h5>Tinas de Fermentación Registradas:</h5>
                          {selectedLot.molienda.items?.map((it, idx) => (
                            <div key={idx} className="sublist-row">
                              <span>• {it.contenedor}</span>
                              <span>{it.kgMaguey} kg maguey</span>
                              <span>{it.volumenFormulado} L agua/mosto</span>
                              <strong>{it.volumenOrdinario} L ordinario ({it.pctAlcOrdinario}%)</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="step-empty-text">Etapa aún no registrada para este lote.</p>
                    )}
                  </div>
                </div>

                {/* FASE 3: DESTILACIÓN */}
                <div className={`timeline-card ${selectedLot.destilacion ? 'active-step' : 'pending-step'}`}>
                  <div className="timeline-node">
                    <WineBarIcon />
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-head">
                      <h4>3. Sección III (B) & IV: 2da Destilación (Refinado)</h4>
                      {selectedLot.destilacion ? (
                        <span className="badge-step-done">Completada</span>
                      ) : (
                        <span className="badge-step-pending">En espera</span>
                      )}
                    </div>
                    {selectedLot.destilacion ? (
                      <div>
                        <div className="timeline-grid-3">
                          <div><strong>Alambique:</strong> {selectedLot.destilacion.tipoAlambique}</div>
                          <div><strong>Carga Destilada:</strong> {selectedLot.destilacion.totalVolCargado?.toLocaleString()} L</div>
                          <div><strong>Puntas:</strong> {selectedLot.destilacion.totalPuntas} L</div>
                          <div><strong>Mezcal Corazón:</strong> <span style={{ color: "#7C3AED", fontWeight: "bold" }}>{selectedLot.destilacion.totalMezcalObtenido?.toLocaleString()} L</span></div>
                          <div><strong>Graduación Promedio:</strong> {selectedLot.destilacion.avgAlcMezcal}% Alc.</div>
                          <div><strong>Colas:</strong> {selectedLot.destilacion.totalColas} L</div>
                        </div>

                        <div className="timeline-sublist">
                          <h5>Corridas de Alambiques:</h5>
                          {selectedLot.destilacion.items?.map((it, idx) => (
                            <div key={idx} className="sublist-row">
                              <span>• {it.destiladorNum} ({it.fechaSegunda})</span>
                              <span>Carga: {it.volADestilar} L</span>
                              <strong>Mezcal: {it.mezcalVol} L ({it.mezcalAlc}%)</strong>
                              <span>Puntas: {it.puntasVol} L • Colas: {it.colasVol} L</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="step-empty-text">Etapa aún no registrada para este lote.</p>
                    )}
                  </div>
                </div>

                {/* FASE 4: GRANEL */}
                <div className={`timeline-card ${selectedLot.granel ? 'active-step' : 'pending-step'}`}>
                  <div className="timeline-node">
                    <CheckCircleIcon />
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-head">
                      <h4>4. Sección V: Cierre e Inventario a Granel</h4>
                      {selectedLot.granel ? (
                        <span className="badge-step-done">Completada</span>
                      ) : (
                        <span className="badge-step-pending">En espera</span>
                      )}
                    </div>
                    {selectedLot.granel ? (
                      <div className="timeline-grid-3">
                        <div><strong>Contenedor Destino:</strong> {selectedLot.granel.idContenedor}</div>
                        <div><strong>Categoría & Clase:</strong> {selectedLot.granel.categoria} - {selectedLot.granel.clase}</div>
                        <div><strong>No. Análisis:</strong> {selectedLot.granel.noAnalisis}</div>
                        <div><strong>Volumen Final:</strong> <span style={{ color: "#059669", fontWeight: "bold" }}>{selectedLot.granel.volFinal?.toLocaleString()} L</span></div>
                        <div><strong>Graduación Final:</strong> {selectedLot.granel.alcFinal}% Alc.</div>
                        <div><strong>Ajuste de Agua:</strong> {selectedLot.granel.volAguaAjuste} L</div>
                      </div>
                    ) : (
                      <p className="step-empty-text">Etapa de inventario a granel pendiente.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer-custom">
              <button type="button" className="btn-cancel-modal" onClick={() => setActiveModal(null)}>
                Cerrar Expediente
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          MODAL 6: DICTAMEN / VALIDACIÓN UI (SIMULADOR DE INSPECTOR)
      ========================================================================= */}
      {activeModal === "dictamen" && selectedLot && createPortal(
        <div className="modal-backdrop-custom">
          <div className="modal-dialog-custom modal-md animate-scaleIn">
            <div className="modal-header-custom header-theme-dictamen">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <FactCheckIcon />
                </div>
                <div>
                  <h3>Dictamen de Inspección UI</h3>
                  <p>Lote: <strong>{selectedLot.noProduccion}</strong> • Etapa: <strong>{selectedLot.etapaActual.toUpperCase()}</strong></p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setActiveModal(null)}>
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={handleSaveDictamen} className="modal-form-custom">
              <div className="form-group-custom">
                <label>Resultado de Validación UI *</label>
                <div className="status-radio-group">
                  <label className={`radio-card ${formDictamen.statusUI === "aprobado_ui" ? "selected-approved" : ""}`}>
                    <input
                      type="radio"
                      name="statusUI"
                      value="aprobado_ui"
                      checked={formDictamen.statusUI === "aprobado_ui"}
                      onChange={(e) => setFormDictamen(prev => ({ ...prev, statusUI: e.target.value }))}
                    />
                    <div>
                      <strong>Aceptar / Validar por UI</strong>
                      <span>Habilita la siguiente fase de trazabilidad</span>
                    </div>
                  </label>

                  <label className={`radio-card ${formDictamen.statusUI === "rechazado" ? "selected-rejected" : ""}`}>
                    <input
                      type="radio"
                      name="statusUI"
                      value="rechazado"
                      checked={formDictamen.statusUI === "rechazado"}
                      onChange={(e) => setFormDictamen(prev => ({ ...prev, statusUI: e.target.value }))}
                    />
                    <div>
                      <strong>Rechazar con Observaciones</strong>
                      <span>El cliente debe corregir antes de avanzar</span>
                    </div>
                  </label>

                  <label className={`radio-card ${formDictamen.statusUI === "pendiente_ui" ? "selected-pending" : ""}`}>
                    <input
                      type="radio"
                      name="statusUI"
                      value="pendiente_ui"
                      checked={formDictamen.statusUI === "pendiente_ui"}
                      onChange={(e) => setFormDictamen(prev => ({ ...prev, statusUI: e.target.value }))}
                    />
                    <div>
                      <strong>Dejar Pendiente de V.Bo.</strong>
                      <span>En revisión técnica</span>
                    </div>
                  </label>
                </div>
              </div>

              <div className="form-group-custom">
                <label>Inspector Autorizado</label>
                <input
                  type="text"
                  value={formDictamen.inspectorName}
                  onChange={(e) => setFormDictamen(prev => ({ ...prev, inspectorName: e.target.value }))}
                />
              </div>

              <div className="form-group-custom">
                <label>Observaciones del Dictamen</label>
                <textarea
                  rows={3}
                  value={formDictamen.observacionesInspector}
                  onChange={(e) => setFormDictamen(prev => ({ ...prev, observacionesInspector: e.target.value }))}
                  placeholder="Detalles de la inspección física o correcciones requeridas..."
                />
              </div>

              <div className="modal-footer-custom">
                <button type="button" className="btn-cancel-modal" onClick={() => setActiveModal(null)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-submit-modal btn-dictamen-submit">
                  Guardar Dictamen UI
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default BitacoraProduccion;